import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Alert, Stack } from '@mui/material';
import { createStock } from '../../services/stocksApi';
import { InvestmentType, CreateStockCommand, CurrencyCode } from '../../types/stocks';

interface AddETFDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void; // callback to refresh list
  existingSymbols?: string[]; // for duplicate validation
}

const AddETFDialog: React.FC<AddETFDialogProps> = ({ open, onClose, onCreated, existingSymbols = [] }) => {
  const [symbol, setSymbol] = useState('');
  const [currentPrice, setCurrentPrice] = useState<string>('');
  const [currency, setCurrency] = useState<CurrencyCode>(CurrencyCode.EUR);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setSymbol('');
    setCurrentPrice('');
    setCurrency(CurrencyCode.EUR);
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  const handleSubmit = async () => {
    setError(null);
    const trimmed = symbol.trim().toUpperCase();
    if (!trimmed) {
      setError('O símbolo é obrigatório');
      return;
    }
    if (existingSymbols.map(s => s.toUpperCase()).includes(trimmed)) {
      setError('O símbolo já existe');
      return;
    }
    setSubmitting(true);
    try {
      const command: CreateStockCommand = {
        symbol: trimmed,
        investmentType: InvestmentType.ETF,
        currentPrice: currentPrice ? parseFloat(currentPrice) : undefined,
        currency: currency
      };
      await createStock(command);
      onCreated();
      reset();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Não foi possível criar o ETF');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Adicionar ETF</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Símbolo"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            autoFocus
            required
            inputProps={{ maxLength: 10 }}
            helperText="Símbolo do título (ex.: SPY, VOO)"
          />
          <TextField
            label="Preço atual (opcional)"
            value={currentPrice}
            type="number"
            onChange={(e) => setCurrentPrice(e.target.value)}
            inputProps={{ step: '0.01', min: '0' }}
          />
          <TextField
            label="Moeda"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as CurrencyCode)}
            select
            helperText="Moeda de negociação deste ETF"
          >
            {Object.values(CurrencyCode).map(c => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Tipo de investimento"
            value={InvestmentType.ETF}
            select
            disabled
            helperText="Fixo como ETF"
          >
            <MenuItem value={InvestmentType.ETF}>ETF</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>Adicionar ETF</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddETFDialog;